import { Close } from "@mui/icons-material";
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material";
import { Buffer } from "buffer";
import saveAs from "file-saver";
import React, { useEffect, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import useFetch from "src/Components/Common/useFetch";
import ReactTable, { ColDef } from "src/Components/ReactTable/ReactTable";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";

const ViewReconcileSummaryModal = ({ open, setOpen, taskDetails }) => {
  const [reconciliationData, setReconciliationData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [isDownReconSummaryReport, setIsDownReconSummaryReport] = useState(false);
  const [mappingId, setMappingId] = useState(null);

  const columnDefinitionReconciliationVendorPortal = React.useMemo(
    (): ColDef<any>[] => [
      {
        header: "Particulars",

        accessorFn: (row) => row.gapDescription || "",
        size: 280,
      },
      {
        header: "Annexures",
        accessorFn: (row) => row.annexures || "",
        size: 300,
      },

      {
        header: "Action Owner",
        accessorFn: (row) => row.actionOwner || "",
        size: 300,
      },
      {
        header: "Own Amount",

        accessorFn: (row) => row.ownAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.ownAmount ? formatMoney(ToDineroObj(row.original?.ownAmount)) : ""}
          >
            {row.original?.ownAmount ? formatMoney(ToDineroObj(row.original?.ownAmount)) : "-"}
          </div>
        ),
      },
      {
        header: "Partner Amount",

        accessorFn: (row) => row.partnerAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.partnerAmount ? formatMoney(ToDineroObj(row.original?.partnerAmount)) : ""}
          >
            {row.original?.partnerAmount ? formatMoney(ToDineroObj(row.original?.partnerAmount)) : "-"}
          </div>
        ),
      },
      {
        header: "Amount Difference",

        accessorFn: (row) => row.amountDifference || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.amountDifference ? formatMoney(ToDineroObj(row.original?.amountDifference)) : ""}
          >
            {row.original?.amountDifference ? formatMoney(ToDineroObj(row.original?.amountDifference)) : "-"}
          </div>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    listReconSummaryOverview();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const listReconSummaryOverview = () => {
    setLoader(true);
    useFetch(API_ENDPOINTS.TASK_BOARD_RECON_SUMMARY_DATA.url, "GET", {
      failureMessage: API_ENDPOINTS.TASK_BOARD_RECON_SUMMARY_DATA.failureMessage,
      config: {
        params: {
          ticketID: taskDetails.taskId,
        },
      },
      thenCallBack: (response) => {
        setReconciliationData(response.data.result.reconciliationData || []);
        setMappingId(response.data.result.mappingId);

        setLoader(false);
      },
    });
  };

  const downReconSummaryReport = () => {
    setIsDownReconSummaryReport(true);
    useFetch(API_ENDPOINTS.TASK_BOARD_RECON_SUMMARY_DOWNLOAD.url, "POST", {
      failureMessage: API_ENDPOINTS.TASK_BOARD_RECON_SUMMARY_DOWNLOAD.failureMessage,
      data: {
        mappingId,
      },
      thenCallBack: (response) => {
        const filename = `Recon_Summary_Report.xlsm`;
        const excelData = Buffer.from(response.data.reportData, "base64");
        const fileType = "application/vnd.ms-excel.sheet.macroEnabled.12";
        const blob = new Blob([excelData], { type: fileType });
        saveAs(blob, filename);
        setIsDownReconSummaryReport(false);
      },
      catchCallBack: () => {
        setIsDownReconSummaryReport(false);
      },
    });
  };

  return (
    <div>
      <Dialog open={open} maxWidth={DIALOG_MAX_WIDTHS.MD} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Reconciliation Summary</div>
          <div className="vertical_center_align">
            <IconButton
              onClick={() => {
                handleClose();
              }}
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Grid sx={{ mb: 2 }}>
            <ReactTable
              columns={columnDefinitionReconciliationVendorPortal}
              rows={reconciliationData || []}
              loading={loader}
              enableGrouping={true}
              paginationDisplayMode={"default"}
              enableRowSelection={false}
              initialState={{
                showColumnFilters: false,
              }}
              enablePagination={false}
              renderBottomToolbarCustomActions={() => {
                return <div> </div>;
              }}
              dynamicHeight={true} // Enable dynamic height
              // enableToolbarInternalActions={false}
              // enableTopToolbar={false}
              // renderTopToolbar={() => null}
            />
          </Grid>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="text"
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            disabled={isDownReconSummaryReport}
            startIcon={<LoadingIcon loading={isDownReconSummaryReport} />}
            onClick={() => {
              downReconSummaryReport();
            }}
          >
            Download Reconciliation
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewReconcileSummaryModal;
